import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Building2, Zap, Warehouse, TrendingUp, Globe, Server, Leaf } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article2 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-purple-600 text-white mr-4">
              <Building2 className="h-3 w-3 mr-1" />
              Data Centre & Logistics
            </Badge>
            <span className="text-sm text-gray-600">Data Centre Dynamics / Savills</span>
          </div>

          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>7 min read</span>
          </div>

          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.2.title')}
          </h1>

          <img
            src="https://media.datacenterdynamics.com/media/images/GettyImages-11837461681.width-358.jpg"
            alt="European Data Centres and Logistics"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.2.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-purple-50 p-6 rounded-lg my-8 border border-purple-200">
              <h3 className="font-bold text-purple-900 mb-4">
                {language === 'ko' ? '핵심 수치' : 'Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-purple-800">
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Warehouse className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '추가 물류 수요' : 'Additional Logistics Demand'}
                  </h4>
                  <p className="text-2xl font-bold text-purple-600">8.5M sq ft</p>
                  <p className="text-xs">786,130 sqm — FLAPD {language === 'ko' ? '시장' : 'markets'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Zap className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '건설 중 용량' : 'Under Construction'}
                  </h4>
                  <p className="text-2xl font-bold text-blue-600">950 MW</p>
                  <p className="text-xs">FLAPD {language === 'ko' ? '시장 기준' : 'markets'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Server className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'MW당 물류 면적' : 'Logistics per DC MW'}
                  </h4>
                  <p className="text-2xl font-bold text-green-600">8,900 sq ft</p>
                  <p className="text-xs">{language === 'ko' ? '평균 추정치' : 'Average estimate'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.2.section1.title')}
            </h2>
            <p className="mb-6">{t('article.2.p1')}</p>
            <p className="mb-6">{t('article.2.p2')}</p>

            {/* DC Growth Timeline */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? '글로벌 데이터센터 전력 용량 성장' : 'Global DC Power Capacity Growth'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
                <div className="text-center bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">2015</p>
                  <p className="text-2xl font-bold text-blue-500">26 GW</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">2024</p>
                  <p className="text-2xl font-bold text-blue-700">81 GW</p>
                  <p className="text-xs text-green-600">+200%</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">2030 {language === 'ko' ? '예측' : 'Forecast'}</p>
                  <p className="text-2xl font-bold text-purple-700">222 GW</p>
                  <p className="text-xs text-green-600">+18% p.a.</p>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-3">
                {language === 'ko' ? '출처: Macquarie — 에너지 수요 연간 15~17% 증가 예상' : 'Source: Macquarie — Energy demand expected to grow 15–17% annually'}
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.2.section2.title')}
            </h2>
            <p className="mb-6">{t('article.2.p3')}</p>
            <p className="mb-6">{t('article.2.p4')}</p>

            {/* FLAPD Market Breakdown */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'FLAPD 시장 유럽 데이터센터 현황' : 'European DC Market — FLAPD Overview'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{language === 'ko' ? '시장' : 'Market'}</th>
                      <th className="text-left py-2">{language === 'ko' ? '현황' : 'Status'}</th>
                      <th className="text-left py-2">{language === 'ko' ? '특이사항' : 'Note'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">Frankfurt</td>
                      <td className="py-2 text-orange-600">{language === 'ko' ? '전력망 혼잡' : 'Grid congestion'}</td>
                      <td className="py-2 text-xs">{language === 'ko' ? '확장 지연' : 'Expansion delays'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">London</td>
                      <td className="py-2 text-orange-600">{language === 'ko' ? '전력망 혼잡' : 'Grid congestion'}</td>
                      <td className="py-2 text-xs">{language === 'ko' ? '정책 리스크 증가' : 'Rising policy risk'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">Amsterdam</td>
                      <td className="py-2 text-orange-600">{language === 'ko' ? '전력망 혼잡' : 'Grid congestion'}</td>
                      <td className="py-2 text-xs">{language === 'ko' ? '50% 운영사 재생에너지 투자 계획' : '50% operators plan on-site renewables'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">Paris</td>
                      <td className="py-2 text-orange-600">{language === 'ko' ? '전력망 혼잡' : 'Grid congestion'}</td>
                      <td className="py-2 text-xs">{language === 'ko' ? '확장 저해' : 'Hampered expansion'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-semibold">Dublin</td>
                      <td className="py-2 text-green-600">{language === 'ko' ? '모라토리엄 종료' : 'Moratorium ended'}</td>
                      <td className="py-2 text-xs">{language === 'ko' ? '신규 법률: 재생에너지 80% 의무화' : 'New law: 80% renewable energy required'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 grid md:grid-cols-3 gap-3 text-xs text-gray-700">
                <div className="bg-white rounded p-2 text-center">
                  <p className="font-bold text-lg text-blue-600">10,990 MW</p>
                  <p>{language === 'ko' ? '현재 가동 중' : 'Live capacity'}</p>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <p className="font-bold text-lg text-orange-600">2,449 MW</p>
                  <p>{language === 'ko' ? '건설 중' : 'Under construction'}</p>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <p className="font-bold text-lg text-purple-600">11,191 MW</p>
                  <p>{language === 'ko' ? '확정 예정' : 'Committed pipeline'}</p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.2.section3.title')}
            </h2>
            <p className="mb-6">{t('article.2.p5')}</p>
            <p className="mb-6">{t('article.2.p6')}</p>

            {/* DHL US Model */}
            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-200">
              <h3 className="font-bold text-green-900 mb-4 flex items-center">
                <Warehouse className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'DC 전용 물류 창고 — 사례 연구' : 'Dedicated DC Logistics Warehousing — Case Studies'}
              </h3>
              <div className="space-y-4 text-sm text-green-800">
                <div className="bg-white p-3 rounded">
                  <p className="font-semibold mb-1">🇺🇸 DHL (USA)</p>
                  <p>• {language === 'ko' ? '전용 부지 10개, 총 700만 평방피트' : '10 dedicated sites, 7 million sq ft total'}</p>
                  <p>• {language === 'ko' ? '서버, 전력 모듈, 네트워킹 시스템 보관' : 'Storage of servers, power modules, networking systems'}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="font-semibold mb-1">🇮🇪 Dublin</p>
                  <p>• {language === 'ko' ? '2025년 DC 관련 물류 임차 비중 10% (225,000 평방피트)' : '10% of annual logistics take-up in 2025 (225,000 sq ft)'}</p>
                  <p>• {language === 'ko' ? '추가 50만 평방피트 협상 중' : 'Further 500,000 sq ft under offer'}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="font-semibold mb-1">🇬🇧 UK — AVK SEG</p>
                  <p>• {language === 'ko' ? 'DC 전력 솔루션 및 AI 인프라 기업' : 'DC power solutions & AI infrastructure firm'}</p>
                  <p>• {language === 'ko' ? '영국 북서부 140,000 평방피트 임차' : '140,000 sq ft leased in the North West'}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="font-semibold mb-1">🇺🇸 Houston (Q4 2025)</p>
                  <p>• {language === 'ko' ? '상위 5대 거래 중 3건이 DC 또는 전력망 관련 수요' : '3 of top 5 deals driven by DC or energy grid demand'}</p>
                  <p>• {language === 'ko' ? 'H2 2025 임차 규모의 11% 해당' : 'Accounted for 11% of take-up in H2 2025'}</p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.2.section4.title')}
            </h2>
            <p className="mb-6">{t('article.2.p7')}</p>
            <p className="mb-6">{t('article.2.p8')}</p>

            {/* Impact Calculation */}
            <div className="bg-yellow-50 p-6 rounded-lg my-8 border border-yellow-200">
              <h3 className="font-bold text-yellow-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'FLAPD 물류 수요 영향 추정 (Savills)' : 'Savills FLAPD Logistics Demand Estimate'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{language === 'ko' ? '기준 시장' : 'Reference Market'}</th>
                      <th className="text-left py-2">{language === 'ko' ? '건설 중 MW' : 'MW Under Construction'}</th>
                      <th className="text-left py-2">{language === 'ko' ? 'sq ft / MW' : 'sq ft / MW'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">Dublin</td>
                      <td className="py-2">71 MW</td>
                      <td className="py-2">7,250–10,560</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">Houston</td>
                      <td className="py-2">250 MW</td>
                      <td className="py-2">7,250–10,560</td>
                    </tr>
                    <tr className="border-b bg-yellow-100">
                      <td className="py-2 font-semibold">{language === 'ko' ? '평균' : 'Average'}</td>
                      <td className="py-2">—</td>
                      <td className="py-2 font-bold text-yellow-800">8,900 sq ft / MW</td>
                    </tr>
                    <tr className="bg-purple-100">
                      <td className="py-2 font-bold text-purple-900">FLAPD {language === 'ko' ? '합계' : 'Total'}</td>
                      <td className="py-2 font-bold text-purple-900">950 MW</td>
                      <td className="py-2 font-bold text-purple-900">8,462,000 sq ft<br/><span className="text-xs">(786,130 sqm)</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Netherlands highlight */}
            <div className="bg-teal-50 p-6 rounded-lg my-8 border border-teal-200">
              <h3 className="font-bold text-teal-900 mb-4 flex items-center">
                <Leaf className="mr-2 h-5 w-5" />
                {language === 'ko' ? '네덜란드: 재생에너지 및 열 재활용 트렌드' : 'Netherlands: Renewables & Heat Reuse Trends'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-teal-800">
                <div className="bg-white rounded p-3">
                  <p className="text-2xl font-bold text-teal-600">50%</p>
                  <p>{language === 'ko' ? '운영사가 현장 재생에너지 투자 계획' : 'Operators plan to invest in on-site renewables'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="text-2xl font-bold text-teal-600">60%</p>
                  <p>{language === 'ko' ? '운영사가 열 수출 규정 준수 준비 완료' : 'Operators ready to export heat for compliance'}</p>
                </div>
              </div>
              <p className="text-xs text-teal-700 mt-3">
                {language === 'ko' ? '출처: Dutch Data Centre Association — 부품·자재·물류 창고 추가 수요 촉진' : 'Source: Dutch Data Centre Association — Driving additional demand for components, materials & warehousing'}
              </p>
            </div>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? 'Savills, 유럽 DC 성장으로 FLAPD 시장에서 850만 평방피트(786,130㎡) 추가 물류 수요 예측' : 'Savills projects 8.5M sq ft (786,130 sqm) additional logistics demand from DC growth across FLAPD markets'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '유럽 DC 시장: 가동 10,990 MW, 건설 중 2,449 MW, 확정 11,191 MW' : 'European DC market: 10,990 MW live, 2,449 MW under construction, 11,191 MW committed'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? 'DC 섹터는 서버, 전력 모듈, 네트워킹 장비 보관을 위한 전용 물류 창고 공급망 구축 중' : 'DC sector developing dedicated logistics warehousing supply chain for servers, power modules and networking equipment'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '더블린 2025년 물류 임차 10%가 DC 관련 수요, 미국 휴스턴 H2 2025 상위 거래 11%도 동일' : 'Dublin 10% of logistics take-up in 2025 from DC sector; Houston H2 2025 top deals 11% DC-driven'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? 'Savills 추정: DC MW당 평균 8,900 평방피트의 물류 공간 수요 발생' : 'Savills estimates 8,900 sq ft of logistics space per DC MW as average ratio'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? 'FLAPD 전력망 혼잡이 DC 확장을 저해 — 더블린은 3년 모라토리엄 종료, 신규 법률로 재생에너지 80% 의무화' : 'FLAPD grid congestion hampering DC expansion — Dublin ended 3-year moratorium, new law requires 80% renewables'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? 'DC와 물류 시설이 공간 경쟁 — 투기적 창고가 DC로 전환되고 물류 공급 파이프라인 토지가 감소' : 'DCs and logistics competing for space — speculative warehouses converted to DCs, logistics land pipeline shrinking'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Data Centre Dynamics / Savills — European data centers to drive surge in logistics space demand.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://www.datacenterdynamics.com/en/news/savills-european-data-centers-to-drive-surge-in-logistics-space-demand-over-the-next-three-years/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article2;
